import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Modal, Radio, Select, Spin, Button, Typography, InputNumber, Space } from 'antd';
import { useGetPrimaveraTasksSmall, useGetTkfActivityCodeTypes, useGetTkfActivityCodeValues, useGetTkfTaskIdsByActivityCode } from '../../hooks/genericHooks';
import type { PrimaveraTaskSmall } from '../../types';
import WbsSelectModal from './WbsSelectModal';

interface CombinedActivitySelectorModalProps {
  open: boolean;
  value?: any;
  onOk: (value: PrimaveraTaskSmall) => void;
  onCancel: () => void;
  options?: any[];
}

// No local tree; WBS selection is delegated to WbsSelectModal

const CombinedActivitySelectorModal: React.FC<CombinedActivitySelectorModalProps> = ({ open, value, onOk, onCancel }) => {
  const WIDTH_STORAGE_KEY = 'CombinedActivitySelectorModal:width';
  const DEFAULT_WIDTH = 520;
  const MIN_WIDTH = 420;
  const MAX_WIDTH = 1200;
  const PARENT_WRAP_CLASS = 'combined-activity-selector-modal';
  const WBS_MODAL_GAP_PX = 8;

  const [mode, setMode] = useState<'wbs' | 'all'>('all');
  const { data: allTasks, isLoading: isLoadingTasks } = useGetPrimaveraTasksSmall();
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(value ? String(value) : null);
  const [selectedWbs, setSelectedWbs] = useState<any>(null);
  const [wbsModalOpen, setWbsModalOpen] = useState(false);
  const lastSelectRef = useRef<{ id: string; at: number } | null>(null);
  const [wbsActivitySelectOpen, setWbsActivitySelectOpen] = useState(false);
  const [wbsModalDismissed, setWbsModalDismissed] = useState(false);
  const [wbsModalTop, setWbsModalTop] = useState<number | undefined>(undefined);
  const [selectedActvCodeTypeId, setSelectedActvCodeTypeId] = useState<number | null>(null);
  const [selectedActvCodeId, setSelectedActvCodeId] = useState<number | null>(null);
  const [modalWidth, setModalWidth] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed)) {
        return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed));
      }
    } catch {
      // ignore
    }
    return DEFAULT_WIDTH;
  });

  useEffect(() => {
    setSelectedActivityId(value ? String(value) : null);
  }, [value]);

  useEffect(() => {
    if (!open) {
      setMode('all');
      setSelectedWbs(null);
      setSelectedActivityId(null);
      setWbsModalDismissed(false);
      setWbsModalTop(undefined);
      setSelectedActvCodeTypeId(null);
      setSelectedActvCodeId(null);
    }
  }, [open]);

  useEffect(() => {
    setSelectedActvCodeId(null);
  }, [selectedActvCodeTypeId]);

  const { data: actvCodeTypesResp, isLoading: isLoadingActvCodeTypes } = useGetTkfActivityCodeTypes();
  const { data: actvCodeValuesResp, isLoading: isLoadingActvCodeValues } = useGetTkfActivityCodeValues(selectedActvCodeTypeId);
  const { data: taskIdsResp, isLoading: isLoadingTaskIds } = useGetTkfTaskIdsByActivityCode({
    actv_code_type_id: selectedActvCodeTypeId,
    actv_code_id: selectedActvCodeId,
  });

  useEffect(() => {
    if (!open) return;
    if (!wbsModalOpen) return;

    const computeTop = () => {
      const modalEl = document.querySelector(`.${PARENT_WRAP_CLASS} .ant-modal`) as HTMLElement | null;
      if (!modalEl) return;

      const rect = modalEl.getBoundingClientRect();
      const desiredTop = Math.round(rect.bottom + WBS_MODAL_GAP_PX);
      const viewportH = window.innerHeight || 0;
      const maxTop = Math.max(24, viewportH - 200);
      const clampedTop = Math.min(desiredTop, maxTop);
      setWbsModalTop(clampedTop);
    };

    computeTop();
    window.addEventListener('resize', computeTop);
    return () => window.removeEventListener('resize', computeTop);
  }, [open, wbsModalOpen]);

  useEffect(() => {
    if (!open) return;
    if (mode !== 'wbs') return;
    if (selectedWbs?.wbs_id) return;
    if (wbsModalOpen) return;
    if (wbsModalDismissed) return;
    setWbsModalOpen(true);
  }, [open, mode, selectedWbs, wbsModalOpen, wbsModalDismissed]);

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(modalWidth));
    } catch {
      // ignore
    }
  }, [modalWidth]);

  const activitiesByWbs = useMemo(() => {
    if (!selectedWbs?.wbs_id || !Array.isArray(allTasks?.data)) return [];
    const wbsId = String(selectedWbs.wbs_id);
    return (allTasks!.data as PrimaveraTaskSmall[]).filter(a => String(a.wbs_id ?? '') === wbsId);
  }, [selectedWbs, allTasks]);

  // Defensive: ensure allActivities is always an array
  const allActivities: PrimaveraTaskSmall[] = Array.isArray(allTasks?.data) ? allTasks!.data : [];

  const unwrapArr = (resp: any): any[] => {
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.data?.data)) return resp.data.data;
    return [];
  };

  const activityCodeTypeOptions = useMemo(() => {
    const rows = unwrapArr(actvCodeTypesResp);
    return rows
      .filter((r: any) => r?.actv_code_type_id != null)
      .map((r: any) => ({
        value: Number(r.actv_code_type_id),
        label: String(r.actv_code_type ?? r.actv_code_type_id),
      }));
  }, [actvCodeTypesResp]);

  const activityCodeValueOptions = useMemo(() => {
    const rows = unwrapArr(actvCodeValuesResp);
    return rows
      .filter((r: any) => r?.actv_code_id != null)
      .map((r: any) => ({
        value: Number(r.actv_code_id),
        label: `${String(r.actv_code ?? r.actv_code_id)}${r?.actv_name ? ` - ${String(r.actv_name)}` : ''}`,
      }));
  }, [actvCodeValuesResp]);

  const allowedTaskIdsSet = useMemo(() => {
    if (selectedActvCodeTypeId == null || selectedActvCodeId == null) return null;
    const rows = unwrapArr(taskIdsResp);
    const s = new Set<string>();
    for (const r of rows) {
      if (r?.task_id != null) s.add(String(r.task_id));
    }
    return s;
  }, [selectedActvCodeTypeId, selectedActvCodeId, taskIdsResp]);

  const filteredAllActivities = useMemo(() => {
    if (!allowedTaskIdsSet) return allActivities;
    return allActivities.filter((a) => allowedTaskIdsSet.has(String(a.task_id)));
  }, [allActivities, allowedTaskIdsSet]);

  const filteredActivitiesByWbs = useMemo(() => {
    if (!allowedTaskIdsSet) return activitiesByWbs;
    return activitiesByWbs.filter((a) => allowedTaskIdsSet.has(String(a.task_id)));
  }, [activitiesByWbs, allowedTaskIdsSet]);

  return (
    <Modal
      open={open}
      title="Select Activity"
      width={modalWidth}
      wrapClassName={PARENT_WRAP_CLASS}
      footer={[
        // Left-aligned count when WBS filter applied
        mode === 'wbs' && selectedWbs?.wbs_id ? (
          <Typography.Text key="count" type="secondary" style={{ marginRight: 'auto' }}>
            {activitiesByWbs.length} activities
          </Typography.Text>
        ) : null,
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="ok" type="primary" onClick={() => {
          let selected: PrimaveraTaskSmall | undefined;
          if (mode === 'wbs') {
            selected = activitiesByWbs.find((a: any) => String(a.task_id) === selectedActivityId);
          } else {
            selected = (allActivities || []).find((a: any) => String(a.task_id) === selectedActivityId);
          }
          if (selected) onOk(selected);
        }} disabled={!selectedActivityId}>OK</Button>
      ]}
      onCancel={onCancel}
      destroyOnClose
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Space size={12} align="center" wrap>
          <Radio.Group
            value={mode}
            onChange={(e) => {
              const nextMode = e.target.value as 'wbs' | 'all';
              setMode(nextMode);
              if (nextMode === 'wbs' && !selectedWbs?.wbs_id) {
                setWbsModalDismissed(false);
                setWbsModalOpen(true);
              }
            }}
          >
            <Radio.Button
              value="wbs"
              onClick={() => {
                setWbsModalDismissed(false);
                setWbsModalOpen(true);
              }}
            >
              Select by WBS
            </Radio.Button>
            <Radio.Button value="all">All Activities</Radio.Button>
          </Radio.Group>

          <Select
            style={{ width: 180 }}
            placeholder="Activity Code Type"
            value={selectedActvCodeTypeId ?? undefined}
            allowClear
            onChange={(v) => setSelectedActvCodeTypeId(v != null ? Number(v) : null)}
            options={activityCodeTypeOptions}
            loading={isLoadingActvCodeTypes}
            showSearch
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
          <Select
            style={{ width: 180 }}
            placeholder="Activity Code Value"
            value={selectedActvCodeId ?? undefined}
            allowClear
            onChange={(v) => setSelectedActvCodeId(v != null ? Number(v) : null)}
            options={activityCodeValueOptions}
            loading={isLoadingActvCodeValues}
            disabled={selectedActvCodeTypeId == null}
            showSearch
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Space>

        <Space size={8} align="center">
          <Typography.Text type="secondary">Width</Typography.Text>
          <InputNumber
            min={MIN_WIDTH}
            max={MAX_WIDTH}
            step={20}
            value={modalWidth}
            onChange={v => {
              const n = typeof v === 'number' ? v : DEFAULT_WIDTH;
              setModalWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n)));
            }}
            style={{ width: 75 }}
          />
          <Button
            size="small"
            onClick={() => setModalWidth(DEFAULT_WIDTH)}
            disabled={modalWidth === DEFAULT_WIDTH}
          >
            Reset
          </Button>
        </Space>
      </div>

      <Spin spinning={isLoadingTasks || isLoadingActvCodeTypes || isLoadingActvCodeValues || isLoadingTaskIds}>
        {mode === 'wbs' ? (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              {selectedWbs && (
                <>
                  <Typography.Text type="secondary">
                    Selected WBS: {selectedWbs.wbs_name} (ID: {selectedWbs.wbs_id})
                  </Typography.Text>
                </>
              )}
            </div>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder="Select an activity"
              value={selectedActivityId ?? undefined}
              open={wbsActivitySelectOpen}
              onDropdownVisibleChange={(open) => setWbsActivitySelectOpen(open)}
              onChange={val => setSelectedActivityId(String(val))}
              onSelect={(val) => {
                const id = String(val);
                const now = Date.now();
                const last = lastSelectRef.current;
                const isDouble = last?.id === id && now - last.at <= 450;
                lastSelectRef.current = { id, at: now };
                if (!isDouble) return;

                const selected = filteredActivitiesByWbs.find((a: any) => String(a.task_id) === id);
                if (selected) onOk(selected);
              }}
              optionFilterProp="children"
              filterOption={(input, option) => {
                const haystack = String(option?.children ?? '').toLowerCase();
                const tokens = input.toLowerCase().split(/\s+/).filter(Boolean);
                return tokens.every(t => haystack.includes(t));
              }}
              disabled={!selectedWbs?.wbs_id}
            >
              {filteredActivitiesByWbs.map((act: PrimaveraTaskSmall) => (
                <Select.Option key={String(act.task_id)} value={String(act.task_id)}>
                  {`${act.task_code} - ${act.task_name}`}
                </Select.Option>
              ))}
            </Select>
            <WbsSelectModal
              open={wbsModalOpen}
              modalProps={{
                centered: false,
                style: wbsModalTop != null ? { top: wbsModalTop } : undefined,
              }}
              onOk={wbs => {
                setMode('wbs');
                setSelectedWbs(wbs);
                setWbsModalOpen(false);
                setWbsModalDismissed(false);
                setSelectedActivityId(null);
                setWbsActivitySelectOpen(true);
              }}
              onCancel={() => {
                setWbsModalOpen(false);
                setWbsModalDismissed(true);
              }}
            />
          </>
        ) : (
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select an activity"
            value={selectedActivityId ?? undefined}
            onChange={val => setSelectedActivityId(String(val))}
            onSelect={(val) => {
              const id = String(val);
              const now = Date.now();
              const last = lastSelectRef.current;
              const isDouble = last?.id === id && now - last.at <= 450;
              lastSelectRef.current = { id, at: now };
              if (!isDouble) return;

              const selected = (filteredAllActivities || []).find((a: any) => String(a.task_id) === id);
              if (selected) onOk(selected);
            }}
            optionFilterProp="children"
            filterOption={(input, option) => {
              const haystack = String(option?.children ?? '').toLowerCase();
              const tokens = input.toLowerCase().split(/\s+/).filter(Boolean);
              return tokens.every(t => haystack.includes(t));
            }}
          >
            {(filteredAllActivities || []).map((act: PrimaveraTaskSmall) => (
              <Select.Option key={String(act.task_id)} value={String(act.task_id)}>
                {`${act.task_code} - ${act.task_name}`}
              </Select.Option>
            ))}
          </Select>
        )}
      </Spin>
    </Modal>
  );
};

export default CombinedActivitySelectorModal;
